import IconComponent from 'components/icon';
import mitt, {
	type Emitter,
	type Handler,
} from 'mitt';
import routes from 'routes';
import * as DialogService from 'services/dialog';
import { AppStateModule } from 'store';
import ScrollBehavior from 'tools/scroll-behavior';
import VueCaretRestore from 'utils/v-caret-restore';
import VueSofthyphenSkipper from 'utils/v-softhyphen-skipper';
import VueHammer, { VueTouch } from 'utils/vue-hammer';
import VueVisible from 'utils/vue-visible';
import type { App } from 'vue';
import {
	createRouter,
	createWebHistory,
	type RouteLocationNormalizedGeneric,
} from 'vue-router';
import VueObserveVisibility from 'vue3-observe-visibility';
import Vue3TouchEvents, { Vue3TouchEventsOptions } from 'vue3-touch-events';

type RouterStateHandlerEvents = {
	'routing-enabled': void;
};

class RouterStateHandler {
	private _emitter: Emitter<RouterStateHandlerEvents> = mitt();

	private _routingEnabled = false;

	public get routingEnabled() {
		return this._routingEnabled;
	}

	public set routingEnabled(value) {
		if (!this._routingEnabled) {
			this._routingEnabled = value;

			if (value) {
				this._emitter.emit('routing-enabled');
			}
		}
	}

	public off<Event extends keyof RouterStateHandlerEvents>(
		event: Event,
		handler: Handler<RouterStateHandlerEvents[Event]>,
	): void {
		this._emitter.off(
			event,
			handler,
		);
	}

	public on<Event extends keyof RouterStateHandlerEvents>(
		event: Event,
		handler: Handler<RouterStateHandlerEvents[Event]>,
	): void {
		this._emitter.on(
			event,
			handler,
		);
	}
}

export function configureRouter(appInstance: App): void {
	appInstance.component(
		'IconComponent',
		IconComponent,
	);
	appInstance.use(VueObserveVisibility);
	appInstance.use(VueCaretRestore);
	appInstance.use(VueSofthyphenSkipper);
	appInstance.use<Vue3TouchEventsOptions>(
		Vue3TouchEvents,
		{},
	);
	appInstance.use(VueHammer);
	appInstance.component(
		'VTouch',
		VueTouch,
	);
	appInstance.use(VueVisible);

	const router = createRouter({
		history: createWebHistory('/app'),
		scrollBehavior(to, _from, savedPosition) {
			if (
				to.hash
				&& to.hash !== '#'
			) {
				return {
					el: to.hash,
				};
			}

			if (
				to.name == 'Pages Overview'
				&& savedPosition
			) {
				return savedPosition;
			}

			return {
				left: 0,
				top: 0,
			};
		},
		routes,
	});
	let pendingRouteTo: RouteLocationNormalizedGeneric | undefined;
	let beforeRoutingEnabled: (() => void) | undefined = undefined;
	const onRoutingEnabledCallback = () => {
		routerStateHandler.off(
			'routing-enabled',
			onRoutingEnabledCallback,
		);
		beforeRoutingEnabled?.();

		if (pendingRouteTo) {
			router.replace(pendingRouteTo);
			pendingRouteTo = undefined;
		}
	};
	/**
	 * We need to wait only once for the first time for the app to be ready,
	 * before we can enable routing.
	 * After that, we can enable routing and navigate to the pending route.
	 * If not, then for example if a route needs the user to be logged in and
	 * this didn't happen yet, the login dialog will be shown even if the user
	 * is already logged in (since the token was not yet validated and the user
	 * was not loaded yet to the Store).
	 */
	beforeRoutingEnabled = router.beforeEach((to) => {
		/**
		 * If the routing is already enabled, we can navigate to the route.
		 */
		if (routerStateHandler.routingEnabled) {
			beforeRoutingEnabled?.();

			return to;
		}

		/**
		 * If not, then we save the route to navigate to it once the routing is enabled.
		 * If the `$router.replace()` is called before the routing is enabled, then this
		 * means some logic is trying to change something in the route, for example
		 * removing a query parameter, so we update the `pendingRouteTo` with the new route
		 * in case the `pendingRouteTo` already has a value set.
		 */
		pendingRouteTo = to;
		routerStateHandler.off(
			'routing-enabled',
			onRoutingEnabledCallback,
		);
		routerStateHandler.on(
			'routing-enabled',
			onRoutingEnabledCallback,
		);

		return false;
	});
	router.onError((err) => {
		if (!AppStateModule.online) {
			DialogService.openErrorDialog({
				header: {
					title: window.App.router.$t('dialogHeaderOffline'),
				},
				body: {
					content: window.App.router.$t('dialogTextLoadError'),
				},
			});
		} else if (err?.message) {
			let dialogErrorText = window.App.router.$t('dialogTextError');
			dialogErrorText += `\n\nError: ${err.message}`;
			DialogService.openErrorDialog({
				body: {
					content: dialogErrorText,
				},
			});
		}
	});
	appInstance.use(router);
	appInstance.use(
		ScrollBehavior,
		{
			router,
			delay: 100,
		},
	);
}

export function enableRouting() {
	routerStateHandler.routingEnabled = true;
}

export const routerStateHandler = new RouterStateHandler();
